import React, { useState, useEffect, lazy, Suspense } from "react";
import { BrowserRouter, Routes, Route } from "react-router-dom";
import Navbar from "@/components/layout/Navbar";
import Footer from "@/components/layout/Footer";
import HomeNew from "@/components/sections/HomeNew";
import About from "@/components/sections/About";
import SkillSet from "@/components/sections/SkillSet";
import ContactPage from "@/pages/ContactPage";
import AboutPage from "@/pages/AboutPage";
import WorkPage from "@/pages/WorkPage";
import PrivacyPolicyPage from "@/pages/PrivacyPolicyPage";
import AdminProjectsPage from "@/pages/AdminProjectsPage";
import AdminAllDbsPage from "@/pages/AdminAllDbsPage";
import ResumePage from "@/pages/ResumePage";
import ScrollToTopButton from "@/components/ui/ScrollToTopButton";
import ScrollProgress from "@/components/ui/ScrollProgress";
import LoadingSequence from "@/components/ui/LoadingSequence";
import ErrorBoundary from "@/components/shared/ErrorBoundary";
import { useSmoothScroll } from "@/hooks/useSmoothScroll";
import LatestWork from "@/components/sections/LatestWork";

const ChatAssistant = lazy(() => import("@/components/ui/ChatAssistant"));

const NotFoundPage: React.FC = () => {
  return (
    <div className="min-h-screen bg-primary text-slate-light flex items-center justify-center px-6">
      <div className="text-center max-w-xl">
        <p className="text-sm uppercase tracking-[0.2em] text-slate-medium mb-4">
          404
        </p>
        <h1 className="text-4xl md:text-6xl font-bold mb-5">Page Not Found</h1>
        <p className="text-slate-medium mb-8">
          The page you requested does not exist. Please go back to the homepage.
        </p>
        <a
          href="/"
          className="inline-block border border-slate-medium px-6 py-3 text-sm uppercase tracking-widest hover:bg-slate-light hover:text-primary transition-colors duration-300"
        >
          Back Home
        </a>
      </div>
    </div>
  );
};

const App: React.FC = () => {
  const hasLoaded = sessionStorage.getItem("hasLoaded") === "true";
  const [isLoading, setIsLoading] = useState(!hasLoaded);
  const [showContent, setShowContent] = useState(hasLoaded);

  useSmoothScroll();

  useEffect(() => {
    // Prevent scrolling during loading
    if (isLoading) {
      document.body.style.overflow = "hidden";
      // Also scroll to top during loading
      window.scrollTo(0, 0);
    } else {
      document.body.style.overflow = "";
    }
  }, [isLoading]);

  const handleLoadingComplete = () => {
    sessionStorage.setItem("hasLoaded", "true");
    setIsLoading(false);
    // Small delay then reveal content with fade
    requestAnimationFrame(() => {
      setShowContent(true);
    });
  };

  return (
    <BrowserRouter>
      <ErrorBoundary>
        <Routes>
          {/* ── Standalone pages — no loading screen ── */}
          <Route path="/contact" element={<ContactPage />} />
          <Route path="/about" element={<AboutPage />} />
          <Route path="/work" element={<WorkPage />} />
          <Route path="/resume" element={<ResumePage />} />
          <Route path="/privacy-policy" element={<PrivacyPolicyPage />} />
          <Route path="/manage/:accessKey" element={<AdminProjectsPage />} />
          <Route path="/manage/all-dbs/:accessKey" element={<AdminAllDbsPage />} />

          {/* ── Main portfolio ── */}
          <Route
            path="/"
            element={
              <>
                {isLoading && (
                  <LoadingSequence onComplete={handleLoadingComplete} />
                )}
                <div
                  className={`bg-[#f6f4ef] text-[#181818] leading-relaxed antialiased selection:bg-[#181818] selection:text-white overflow-x-hidden transition-opacity duration-1000 ease-out ${
                    showContent ? "opacity-100" : "opacity-0"
                  }`}
                >
                  <ScrollProgress />
                  <div className="relative z-10">
                    <Navbar />
                    <HomeNew />
                    <main className="container mx-auto px-6 md:px-12 lg:px-24">
                      <About />
                    </main>
                    {/* <LatestWork /> */}
                    <LatestWork/>
                    <div className="container mx-auto px-6 md:px-12 lg:px-24">
                      <SkillSet />
                    </div>
                    <Footer />
                    <ScrollToTopButton />
                    <Suspense fallback={null}>
                      <ChatAssistant />
                    </Suspense>
                  </div>
                </div>
              </>
            }
          />

          <Route path="*" element={<NotFoundPage />} />
        </Routes>
      </ErrorBoundary>
    </BrowserRouter>
  );
};

export default App;
